// «Рейтинг ОП — кто сильнее» — перенос раздела 🏆 с портала 55.
//
// ── Прибыль здесь — та, которую записал бизнес-процесс, а не наша ──
//
// Прибыль сделки = поле «Разница» (UF_CRM_1486892924) из Bitrix, в рублях. Считает её
// бизнес-процесс «Маржа» (52/661), а ядро только складывает — своей формулы прибыли у CRM
// нет и быть не должно.
//
// Так было не всегда. До 27.07.2026 и портал, и этот экран считали `оборот × «Процент
// прибыли» / 100`, и разбор БП (`cir-core/БП_52_маржа.md` §7) показал, что из одной этой
// формулы выходят три неверных ответа сразу:
//
//   • процент округлён до сотых — из него нельзя восстановить рубли;
//   • у 410 сделок он отрицательный на 28.07.2026 (есть −725 819 439,33), поэтому его
//     приходилось зажимать в [0; 100], и убыточная сделка показывалась как «заработали
//     ноль»;
//   • сделки в евро и долларах обнулялись, хотя в сумме сделки лежат уже рубли;
//   • и вся прибыль доставалась одному ответственному, хотя БП делит её между отделом
//     продаж и сервисом — а доля сервиса это больше половины.
//
// ── Про то, что цифра изменилась, экран говорит ПЕРВЫМ ДЕЛОМ ──
//
// Люди носят эти числа на совещания. Рейтинг, который однажды утром стал другим без
// объяснения, стоит доверия ко всей CRM дороже, чем любая верная формула. Поэтому ядро
// считает прежнюю цифру рядом с новой (`total.prev_formula`), а экран показывает
// «было → стало» плашкой над итогом, до всяких таблиц. Плашка временная и уйдёт вместе
// с узлом `prev_formula`.
//
// ── Три отличия от портала, о которых написано прямо на экране ──
//
// 1. ПРИБЫЛЬ. Портал по-прежнему множит оборот на процент как есть. За 2026 год по
//    воронке 4 у него выходит 474,8 млн ₽, у нас 444,0 млн — и это не ошибка ни у кого,
//    это разные величины.
// 2. СОСТАВ УЧАСТНИКОВ. Портал держит 32 фамилии коммерческой службы в коде и делит их на
//    «ОП1», «ОП2», «Механика», «Ростов»… Мы берём настоящую оргструктуру Bitrix и считаем
//    отдел вместе с подотделами. Новичок попадает в рейтинг сам, уволенный не исчезает из
//    истории, а Титов и Титова не путаются.
// 3. ЛИГИ И МЕДАЛИ оставлены как в портале (доля от лидера ≥ 66 % — «Высшая», ≥ 33 % —
//    «Первая», иначе «Вторая»). Это соглашение портала, а не расчёт, и так и подписано.
//
// ── Почему места считаются по ПОЛНОЙ прибыли, а не по доле ОП ──
//
// Доля ОП есть в каждой строке и в каждой карточке отдела. Но ранжировать по ней нельзя:
// на 28.07.2026 деление записано только у 32 285 сделок из 74 655, и у каждого шестого
// участника рейтинга за 2026 год прибыль стала бы нулевой не потому, что он не заработал.
// Плюс сами формулы деления в шаблоне 661 частично битые (§6 того же разбора). Поэтому
// доля показана рядом, а не подставлена вместо — и человек видит оба числа.
//
// ── Про права: рейтинг — самый чувствительный раздел в CRM ──
//
// Это единственный экран, который по замыслу показывает цифры ДРУГИХ людей. Поэтому зону
// видимости он не выбирает и не может: её подставляет BFF на сервере, а ядро без неё
// отвечает отказом. Если человек видит не всех, экран говорит об этом прямо — «рейтинг по
// вашей зоне» честнее, чем таблица, из которой молча вычли половину коллег.
//
// Экран ТОЛЬКО ЧИТАЮЩИЙ: ни одной записи в Bitrix.

import { useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { NotWired, fetchSalesRating } from '../api/analytics'
import type { AnalyticsParams, PeriodKind, RatingRow, SalesRating as SalesRatingData }
  from '../api/analytics'
import { useRefs, useRefsQuery } from '../api/hooks'
import { QueryState } from '../components/QueryState'
import { FilterBar } from '../components/FilterBar'
import {
  PERIODS, byDepartment, commercialDept, deltaKind, fmtDelta, fmtNum, fmtPct, leagueKind,
  shareOfMax,
} from '../lib/analytics'
import { makeOrg, plural } from '../lib/dashboard'
import type { RefsOrgData } from '../lib/dashboard'
import { fmtMoney } from '../lib/format'

export default function SalesRating() {
  const [sp, setSp] = useSearchParams()
  const period = (sp.get('period') || 'year') as PeriodKind

  const refs = useRefs()
  const refsQuery = useRefsQuery()
  // Приведение типа — то же, что в pages/Dashboard.tsx, и по той же причине: ядро отдаёт
  // `departments` в /refs уже сейчас, а в контракте `api/types.ts` этого поля пока нет, и
  // правит тот файл его владелец. Появится там `RefsData.departments` — приведение уйдёт.
  const org = useMemo(
    () => makeOrg(refsQuery.data as unknown as RefsOrgData | undefined), [refsQuery.data])
  const departments = (refsQuery.data as unknown as RefsOrgData | undefined)?.departments

  // Отдел по умолчанию — «Коммерческая служба», как в портале 55. В адресе он живёт явно,
  // чтобы ссылку на срез можно было переслать; `dept=all` — сознательный выбор «вся компания».
  const кс = useMemo(() => commercialDept(departments), [departments])
  const deptParam = sp.get('dept')
  const dept = deptParam ?? (кс ? String(кс.id) : 'all')

  const params: AnalyticsParams = useMemo(() => ({
    period,
    dept: dept === 'all' ? 'all' : Number(dept),
  }), [period, dept])

  const q = useQuery({
    queryKey: ['analytics-rating', params],
    queryFn: () => fetchSalesRating(params),
    staleTime: 5 * 60_000,
    retry: (count, err) => !(err instanceof NotWired) && count < 1,
    placeholderData: (prev) => prev,
    // Пока отделы из /refs не пришли, «Коммерческая служба» не найдена, и запрос ушёл бы
    // по всей компании — то есть показал бы не тот список людей и тут же сменил бы его.
    enabled: refsQuery.isSuccess || deptParam !== null,
  })

  const set = (k: string, v: string) => {
    const next = new URLSearchParams(sp)
    next.set(k, v)
    setSp(next, { replace: true })
  }

  const d = q.data
  const разбивка = useMemo(
    () => (d ? byDepartment(d.rows, org) : null), [d, org])

  return (
    <>
      <div className="page-head">
        <h1>Рейтинг ОП</h1>
        <div className="spacer" />
        <Link to="/analytics">аналитика продаж →</Link>
        <a href="https://55.cirspb.com/#/rating" target="_blank" rel="noreferrer">
          тот же раздел на портале 55 →
        </a>
      </div>

      {q.error instanceof NotWired ? (
        <div className="notice">
          <b>Раздел ещё не подключён.</b> Экран и агрегаты в ядре готовы, но маршрута
          {' '}<span className="mono">POST /api/analytics/rating</span> в BFF пока нет — а
          {' '}именно он подставляет зону видимости. Для рейтинга это не формальность:
          {' '}без зоны раздел показал бы каждому цифры всей компании.
        </div>
      ) : (
        <div className="card" style={{ marginBottom: 16 }}>
          <FilterBar id="sales-rating">
            <div className="f">
              <label htmlFor="rt-period">Период</label>
              <select id="rt-period" value={period} onChange={(e) => set('period', e.target.value)}>
                {PERIODS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}{p.slow ? ' (считается дольше)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="f" style={{ minWidth: 260 }}>
              <label htmlFor="rt-dept">Отдел</label>
              <select id="rt-dept" value={dept} onChange={(e) => set('dept', e.target.value)}
                      disabled={!org.loaded}>
                <option value="all">Вся компания</option>
                {(departments ?? []).filter((x) => !x.deleted).map((x) => (
                  <option key={x.id} value={x.id}>{x.name || `Отдел #${x.id}`}</option>
                ))}
              </select>
            </div>
          </FilterBar>
          <div className="hint" style={{ marginTop: 10 }}>
            {d ? (
              <>
                Период: <b>{d.period.label}</b> — по дате закрытия сделки. Воронка:{' '}
                <b>{d.filter.category === 'all'
                  ? 'все' : refs.categoryName(String(d.filter.category))}</b>.
                {' '}Ядро посчитало за {fmtNum(d.ms)} мс.
                {d.filter.dept && (
                  <> Отдел с подотделами: {d.filter.dept.depts}
                    {' '}{plural(d.filter.dept.depts, 'отдел', 'отдела', 'отделов')},
                    {' '}{d.filter.dept.users.length}
                    {' '}{plural(d.filter.dept.users.length, 'человек', 'человека', 'человек')}
                    {' '}(из них действующих {d.filter.dept.users_active}).</>
                )}
              </>
            ) : 'Считаем…'}
          </div>
          {!deptParam && кс && (
            <div className="hint" style={{ marginTop: 6 }}>
              Открыто по отделу <b>{кс.name}</b>, потому что портал 55 считает рейтинг по
              коммерческой службе. <b>Но это не тот же список людей.</b> Портал держит
              32 фамилии в коде и относит к «своим» в том числе Владивосток, Ростов и
              Механику, а в оргструктуре Bitrix эти люди числятся не под коммерческой
              службой. На живых данных за 2026 год разница такая: по этому отделу
              {' '}24 участника и 228,6 млн ₽ прибыли, по всей компании — 72 участника и
              444,0 млн ₽. Первая тройка в обоих случаях одна и та же. Если сверяете с
              порталом и не сходится число людей — причина здесь, а не в расчёте;
              переключите отдел на «Вся компания».
            </div>
          )}
          {!deptParam && !кс && org.loaded && (
            <div className="hint" style={{ marginTop: 6 }}>
              Отдела с «коммерческ» в названии в оргструктуре Bitrix не нашлось, поэтому
              рейтинг открыт по всей компании. Портал 55 считает его по коммерческой службе,
              так что список людей будет шире портального.
            </div>
          )}
        </div>
      )}

      {!(q.error instanceof NotWired) && (
        <QueryState isLoading={q.isLoading || (!d && !q.error)} error={q.error}>
          {d && разбивка && (
            <>
              {d.filter.scope.kind === 'ids' && (
                <div className="notice">
                  Это рейтинг <b>по вашей зоне видимости</b>: {d.filter.scope.count}
                  {' '}{plural(d.filter.scope.count ?? 0, 'сотрудник', 'сотрудника', 'сотрудников')}
                  {' '}из всей компании. Места и лиги считаются внутри этого списка, поэтому
                  «первое место» здесь означает первое среди тех, кого вам видно, а не в
                  компании. Полный рейтинг видят администратор и бухгалтерия.
                </div>
              )}
              {d.filter.scope.empty && (
                <div className="notice">
                  Ваша учётная запись не связана с сотрудником Bitrix — зона видимости пуста,
                  и в рейтинг не попадает никто. Это не «продаж нет».
                </div>
              )}

              {/* ─────────── ЧТО ИЗМЕНИЛОСЬ ─────────── */}
              {/*
                Плашка стоит ПЕРЕД итогом, а не в сносках внизу: человек, открывший рейтинг
                после правки, увидит другое число раньше, чем дочитает до объяснения. Ядро
                отдаёт оба числа готовыми (`total.prev_formula`), считать разницу здесь
                нельзя — она разошлась бы с расчётом при первой же правке формулы.
              */}
              <ЧтоИзменилось total={d.total} />

              {/* ─────────── ИТОГ ─────────── */}
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="page-head" style={{ marginBottom: 10 }}>
                  <h2>Итог за {d.period.label}</h2>
                  <div className="spacer" />
                  {d.period.prev_label && (
                    <span className="hint">сравнение: {d.period.prev_label}</span>
                  )}
                </div>
                <div className="tiles">
                  <div className="tile">
                    <div className="t-label">Прибыль</div>
                    <div className="t-sum">{fmtMoney(d.total.profit)}</div>
                    <div className="t-label">
                      {d.total.prev_profit === null ? 'сравнивать не с чем' : (
                        <span className={`badge ${deltaKind(d.total.profit_growth)}`}>
                          {fmtDelta(d.total.profit_growth)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="tile">
                    <div className="t-label">Из них доля сервиса</div>
                    <div className="t-sum">{fmtMoney(d.total.profit_service)}</div>
                    <div className="t-label">
                      отдел продаж {fmtMoney(d.total.profit_op)}
                    </div>
                  </div>
                  <div className="tile">
                    <div className="t-label">Оборот</div>
                    <div className="t-sum">{fmtMoney(d.total.revenue)}</div>
                    <div className="t-label">средняя маржа {fmtPct(d.total.margin)}</div>
                  </div>
                  <div className="tile">
                    <div className="t-label">Выиграно сделок</div>
                    <div className="t-sum">{fmtNum(d.total.deals)}</div>
                    <div className="t-label">
                      {d.total.loss_deals > 0
                        ? `из них ${d.total.loss_deals} в минус`
                        : 'закрыты со смыслом «успех»'}
                    </div>
                  </div>
                  <div className="tile">
                    <div className="t-label">Участников</div>
                    <div className="t-sum">{fmtNum(d.rows.length)}</div>
                    <div className="t-label">у кого есть хотя бы одна сделка</div>
                  </div>
                  <div className="tile">
                    <div className="t-label">Лидер</div>
                    <div className="t-sum" style={{ fontSize: 'var(--fs-body)' }}>
                      {d.rows[0] ? refs.userName(d.rows[0].user_id) : '—'}
                    </div>
                    <div className="t-label">
                      {d.rows[0] ? fmtMoney(d.rows[0].profit) : 'нет ни одной сделки'}
                    </div>
                  </div>
                </div>
              </div>

              {d.rows.length === 0 ? (
                <div className="card">
                  <div className="empty">
                    <div className="empty-title">
                      За {d.period.label} выигранных сделок в этой выборке нет
                    </div>
                    <div className="empty-hint">
                      Смените период или отдел. Пусто здесь — это ответ, а не сбой.
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* ─────────── ПОДИУМ ─────────── */}
                  <div className="card" style={{ marginBottom: 16 }}>
                    <h2>Первая тройка</h2>
                    <div className="tiles">
                      {d.rows.slice(0, 3).map((r) => (
                        <div className="tile" key={String(r.user_id)}>
                          <div className="t-label">
                            {r.medal} место {r.place}
                            {' '}<span className={`badge ${leagueKind(r.league)}`}>{r.league}</span>
                          </div>
                          <div className="t-sum" style={{ fontSize: 'var(--fs-body)' }}>
                            {refs.userName(r.user_id)}
                          </div>
                          <div className="t-label">
                            {fmtMoney(r.profit)} · маржа {fmtPct(r.margin)} · {fmtNum(r.deals)}
                            {' '}{plural(r.deals, 'сделка', 'сделки', 'сделок')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ─────────── ТАБЛИЦА МЕСТ ─────────── */}
                  <div className="card" style={{ marginBottom: 16 }}>
                    <h2>Таблица мест</h2>
                    <RatingTable rows={d.rows} userName={refs.userName}
                                 prevLabel={d.period.prev_label} />
                  </div>

                  {/* ─────────── ПО ОТДЕЛАМ ─────────── */}
                  <div className="card" style={{ marginBottom: 16 }}>
                    <div className="page-head" style={{ marginBottom: 10 }}>
                      <h2>По отделам</h2>
                      <div className="spacer" />
                      <span className="hint">оргструктура Bitrix, а не зашитый список</span>
                    </div>
                    {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */}
                    <div className="table-wrap cards">
                      <table>
                        <thead>
                          <tr>
                            <th>Отдел</th>
                            <th className="num">Прибыль</th>
                            <th className="num">в т.ч. Сервис</th>
                            <th className="num">Оборот</th>
                            <th className="num">Маржа</th>
                            <th className="num">Сделок</th>
                            <th className="num">Людей</th>
                          </tr>
                        </thead>
                        <tbody>
                          {разбивка.groups.map((g) => (
                            <tr key={g.dept ? g.dept.id : 'orphans'}>
                              <td data-label="Отдел">{g.dept
                                ? (g.dept.name || `Отдел #${g.dept.id}`)
                                : <span className="muted">Вне оргструктуры</span>}</td>
                              <td className="num" data-label="Прибыль">{fmtMoney(g.profit)}</td>
                              <td className="num" data-label="в т.ч. Сервис">
                                {fmtMoney(g.profit_service)}
                              </td>
                              <td className="num" data-label="Оборот">{fmtMoney(g.revenue)}</td>
                              <td className="num" data-label="Маржа">
                                {fmtPct(g.revenue ? (g.profit / g.revenue) * 100 : null)}
                              </td>
                              <td className="num" data-label="Сделок">{fmtNum(g.deals)}</td>
                              <td className="num" data-label="Людей">{fmtNum(g.rows.length)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="hint" style={{ marginTop: 10 }}>
                      {разбивка.doubled > 0 && (
                        <>
                          <b>Сумма по отделам больше общего итога</b>, и это не ошибка:
                          {' '}{разбивка.doubled}
                          {' '}{plural(разбивка.doubled, 'человек состоит', 'человека состоят',
                            'человек состоят')} более чем в одном отделе, и каждый посчитан в
                          {' '}каждом своём. «Главного» отдела в Bitrix нет вовсе, поэтому
                          {' '}делить его прибыль между отделами было бы выдумкой.{' '}
                        </>
                      )}
                      {разбивка.orphans > 0 && (
                        <>
                          Строка «вне оргструктуры» — {разбивка.orphans}
                          {' '}{plural(разбивка.orphans, 'человек', 'человека', 'человек')},
                          {' '}которых нет в составе отделов: обычно это уволенные (состав
                          отдела в справочнике ядра считается по действующим). Их сделки
                          закрыты и прибыль по ним получена, поэтому из истории мы их
                          не вычёркиваем.{' '}
                        </>
                      )}
                      Портал 55 показывает здесь «ОП1», «ОП2», «Механика», «Ростов» — это его
                      собственные названия из списка в коде, а не отделы Bitrix. Совпадения
                      названий с этой таблицей не ждите; цифры на человека при этом те же.
                    </div>
                  </div>
                </>
              )}

              {/* ─────────── КАК ЭТО ПОСЧИТАНО ─────────── */}
              <div className="card">
                <h2>Как посчитана прибыль</h2>
                <ul className="plain-list">
                  {d.notes.map((n, i) => <li key={i} className="hint">{n}</li>)}
                </ul>
                {d.total.other_currency_count > 0 && (
                  <div className="hint" style={{ marginTop: 8 }}>
                    {d.total.other_currency_count}
                    {' '}{plural(d.total.other_currency_count, 'сделка', 'сделки', 'сделок')}
                    {' '}помечены валютой, отличной от рубля, и <b>посчитаны как рублёвые</b>:
                    {' '}в сумму сделки бизнес-процесс записывает уже рубли. До 27.07.2026
                    {' '}такие сделки обнулялись.
                  </div>
                )}
                <div className="hint" style={{ marginTop: 8 }}>
                  Поля расчёта в Bitrix: прибыль —{' '}
                  <span className="mono">{d.profit_fields.profit}</span> («Разница»), доля
                  отдела продаж — <span className="mono">{d.profit_fields.op}</span>, доля
                  сервиса — <span className="mono">{d.profit_fields.service}</span>. Все
                  значения считает бизнес-процесс «Маржа», руками их не правят — и в CRM они
                  закрыты для записи. Прежняя формула строилась на{' '}
                  <span className="mono">{d.profit_fields.prev_formula}</span> («Процент
                  прибыли»).
                </div>
              </div>
            </>
          )}
        </QueryState>
      )}
    </>
  )
}

/**
 * «Было столько-то — стало столько-то, вот почему».
 *
 * Отдельная плашка, а не строчка в сносках, и стоит она над итогом. Причина простая: цифры
 * этого раздела носят на совещания, и человек, увидевший другое число без объяснения, не
 * пойдёт искать сноску — он решит, что сломалась CRM.
 *
 * Оба числа приходят из ядра готовыми. Считать разницу здесь нельзя: она разошлась бы с
 * расчётом при первой же правке формулы, и разошлась бы молча.
 *
 * Плашка исчезает сама, когда `prev_formula.delta` станет нулевым или узел уберут из ответа
 * ядра — то есть тогда, когда объяснять станет нечего.
 */
function ЧтоИзменилось({ total }: { total: SalesRatingData['total'] }) {
  const было = total.prev_formula
  if (!было || Math.abs(было.delta) < 1) return null
  const меньше = было.delta < 0
  return (
    <div className="notice" style={{ marginBottom: 16 }}>
      <b>Прибыль здесь считается по-новому с 27 июля 2026 года.</b> По прежней формуле
      {' '}(оборот × «Процент прибыли») за этот период вышло бы <b>{fmtMoney(было.profit)}</b>,
      {' '}сейчас — <b>{fmtMoney(total.profit)}</b>: {меньше ? 'меньше' : 'больше'} на
      {' '}{fmtMoney(Math.abs(было.delta))}.
      {' '}Дело не в данных и не в сбое: раньше CRM перемножала оборот на процент, округлённый
      {' '}до сотых, а теперь берёт рубли, которые записал в сделку сам бизнес-процесс
      {' '}«Маржа» — то же число, что видно в карточке сделки и в бухгалтерии.
      {' '}{было.pct_clamped > 0 && (
        <>Заодно перестал требоваться зажим процента в [0; 100]: он был нужен из-за мусора
        {' '}вроде −725 819 439, и по {было.pct_clamped}
        {' '}{plural(было.pct_clamped, 'сделке', 'сделкам', 'сделкам')} этого периода убыток
        {' '}показывался как ноль. </>
      )}
      Портал 55 продолжает считать по-старому, и расхождение с ним — вот это.
    </div>
  )
}

function RatingTable({ rows, userName, prevLabel }: {
  rows: RatingRow[]
  userName: (id?: string | number | null) => string
  prevLabel: string
}) {
  const max = rows.reduce((m, r) => Math.max(m, r.profit), 0)
  return (
    <>
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Сотрудник</th>
              <th>Лига</th>
              <th className="num">Прибыль</th>
              {/*
                Доли ОП и Сервиса — то, чего в рейтинге не было вовсе: вся прибыль сделки
                доставалась одному ответственному. Стоят СРАЗУ за прибылью, а не в конце
                таблицы, потому что на живых данных доля сервиса — больше половины, и
                прятать её за оборотом и маржой значило бы почти не показать.
              */}
              <th className="num">в т.ч. ОП</th>
              <th className="num">в т.ч. Сервис</th>
              <th className="num">Оборот</th>
              <th className="num">Маржа</th>
              <th className="num">Сделок</th>
              <th className="num">Ср. прибыль</th>
              {prevLabel && <th className="num">К прошлому</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const доля = shareOfMax(r.profit, max)
              return (
                <tr key={String(r.user_id)}>
                  <td data-label="#">{r.medal || r.place}</td>
                  <td data-label="Сотрудник" style={{
                    background: `linear-gradient(to right, var(--accent-soft) ${доля}%,`
                      + ` transparent ${доля}%)`,
                  }}>
                    {r.user_id === null
                      ? <span className="muted">Без ответственного</span>
                      : userName(r.user_id)}
                    {r.loss_deals > 0 && (
                      <span className="badge lose" style={{ marginLeft: 6 }}
                            title="Столько сделок закрыто в минус: бизнес-процесс записал
                                   отрицательную «Разницу». Раньше такие сделки показывались
                                   с нулевой прибылью.">
                        в минус: {r.loss_deals}
                      </span>
                    )}
                    {r.no_profit > 0 && (
                      <span className="badge work" style={{ marginLeft: 6 }}
                            title="По стольким сделкам бизнес-процесс прибыль не посчитал
                                   вовсе. Принят ноль, оборот в рейтинге остался. Это не
                                   «сработали в ноль», это «неизвестно».">
                        без расчёта: {r.no_profit}
                      </span>
                    )}
                  </td>
                  <td data-label="Лига">
                    <span className={`badge ${leagueKind(r.league)}`}>{r.league}</span>
                  </td>
                  <td className="num" data-label="Прибыль">{fmtMoney(r.profit)}</td>
                  <td className="num" data-label="в т.ч. ОП">
                    {r.split_deals ? fmtMoney(r.profit_op) : <span className="muted">—</span>}
                  </td>
                  <td className="num" data-label="в т.ч. Сервис">
                    {r.split_deals
                      ? fmtMoney(r.profit_service)
                      : <span className="muted">—</span>}
                  </td>
                  <td className="num" data-label="Оборот">{fmtMoney(r.revenue)}</td>
                  <td className="num" data-label="Маржа">{fmtPct(r.margin)}</td>
                  <td className="num" data-label="Сделок">{fmtNum(r.deals)}</td>
                  <td className="num" data-label="Ср. прибыль">
                    {r.avg_profit === null ? '—' : fmtMoney(r.avg_profit)}
                  </td>
                  {prevLabel && (
                    <td className="num" data-label="К прошлому">
                      {r.prev_place === null ? (
                        <span className="muted" title="В прошлом периоде сделок не было">новый</span>
                      ) : (
                        <>
                          <span className={`badge ${deltaKind(r.profit_growth)}`}>
                            {fmtDelta(r.profit_growth)}
                          </span>
                          {r.place_moved !== null && r.place_moved !== 0 && (
                            <span className="faint" style={{ marginLeft: 6 }}>
                              {r.place_moved > 0 ? `↑${r.place_moved}` : `↓${-r.place_moved}`}
                            </span>
                          )}
                        </>
                      )}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="hint" style={{ marginTop: 10 }}>
        Сортировка — <b>по полной прибыли сделки</b>, как в портале 55. «↑2» значит
        «поднялся на два места относительно {prevLabel || 'прошлого периода'}»; «новый» —
        в прошлом периоде у человека не было ни одной выигранной сделки, и рост считать
        не от чего.
      </div>
      <div className="hint" style={{ marginTop: 6 }}>
        Колонки «в т.ч. ОП» и «в т.ч. Сервис» — это деление прибыли между отделом продаж и
        сервисом, которое делает сам бизнес-процесс («Разница ОП» и «Разница Сервис»).
        {' '}<b>Места по ним не считаются</b>, и это не забывчивость: деление записано не у
        всех сделок, и ранжировать по нему значило бы обнулить тех, у кого его просто нет.
        Прочерк в колонке означает, что по сделкам этого человека деления в Bitrix нет
        вовсе. Сумма долей может не совпасть с прибылью — формулы деления в шаблоне
        бизнес-процесса частично сломаны, и мы показываем их как есть, а не подгоняем.
      </div>
      <div className="hint" style={{ marginTop: 6 }}>
        Значок «в минус» — столько сделок закрыто с отрицательной прибылью. До 27.07.2026
        такие сделки показывались с нулевой прибылью, потому что «Процент прибыли»
        зажимался в [0; 100]; рейтинг человека, сработавшего в минус, выглядел как
        «ничего не заработал». Значок «без расчёта» — бизнес-процесс по этим сделкам
        прибыль не посчитал вовсе: принят ноль, оборот остался.
      </div>
    </>
  )
}
